import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  createTab,
  moveTabToPane,
  removeTab,
  renameTab,
  reorderTabs,
  setActiveTab,
  setSplitTab,
  toggleTabLock,
  type PaneIdx,
  type Tab,
} from "@/state/slices/tabsSlice";
import { setSignalChartOpen } from "@/state/slices/uiSlice";
import { setStrategiesViewOpen } from "@/state/slices/strategiesSlice";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher/WorkspaceSwitcher";
import styles from "./TabBar.module.css";

type MenuItem = {
  label: string;
  action: () => void;
  danger?: boolean;
  disabled?: boolean;
};

/** Tab strip between Header and the main row.
 *
 *  Two layouts:
 *  - **Single pane:** one row of pill tabs (current behaviour).
 *  - **Split (VS Code-style):** two sub-bars side by side at the
 *    same `splitRatio` boundary as the chart panes. Each sub-bar
 *    has its OWN tab list (filtered by `paneByTabId`), its own
 *    "+" for creating tabs in that pane, and its own active-tab
 *    highlight. The trailing WorkspaceSwitcher stays at the far
 *    right in both layouts.
 *
 *  Drag-and-drop within a sub-bar reorders normally. Cross-pane
 *  drag (left-bar tab onto right-bar tab, or vice versa) is
 *  intentionally not supported — the user moves a tab between panes
 *  via the right-click context menu's "Move to <other> pane".
 *  Reason: cross-pane drag UX gets confusing without strong drop-
 *  zone visuals (would need a "land here vs join other pane" hover
 *  state), and the context menu covers the same intent. */
export function TabBar() {
  const dispatch = useAppDispatch();
  const tabs = useAppSelector((s) => s.tabs.tabs);
  const activeTabId = useAppSelector((s) => s.tabs.activeTabId);
  const splitTabId = useAppSelector((s) => s.tabs.splitTabId);
  const paneByTabId = useAppSelector((s) => s.tabs.paneByTabId);
  const splitRatio = useAppSelector((s) => s.tabs.splitRatio);
  const liveTabId = useAppSelector((s) => s.analysis.liveTabId);
  const uiByTab = useAppSelector((s) => s.ui.byTab);
  // Signal chart is a special full-area "tab" (not in the tabs list) — a pill
  // in the left bar toggles it; opening it covers the user's chart, and
  // clicking any real tab returns to that chart. Shown only when enabled.
  const signalEnabled = useAppSelector((s) => s.signal.enabled);
  const signalOpen = useAppSelector((s) => s.ui.signalChartOpen);
  const signalOnline = useAppSelector((s) => s.signal.online);
  // Strategies — the same kind of full-area pill for the Python strategy bots
  // (features/strategy_signals.md). Mutually exclusive with Signal.
  const strategiesEnabled = useAppSelector((s) => s.strategies.enabled);
  const strategiesOpen = useAppSelector((s) => s.strategies.viewOpen);
  const strategiesOnline = useAppSelector((s) => s.strategies.online);
  const strategiesActiveCount = useAppSelector(
    (s) =>
      s.strategies.strategies.filter((x) => x.status.running > 0 || x.status.pending > 0)
        .length,
  );

  const splitActive = splitTabId !== null;

  // Partition tabs by pane. When not split, everything's in the
  // left sub-bar (paneByTabId may still mark some as 1 — those get
  // surfaced when split reopens). When split, left = pane 0, right
  // = pane 1.
  const { leftTabs, rightTabs } = useMemo(() => {
    if (!splitActive) {
      return { leftTabs: tabs, rightTabs: [] as Tab[] };
    }
    const left: Tab[] = [];
    const right: Tab[] = [];
    for (const t of tabs) {
      if (paneByTabId[t.id] === 1) right.push(t);
      else left.push(t);
    }
    return { leftTabs: left, rightTabs: right };
  }, [tabs, paneByTabId, splitActive]);

  // Drag state — shared across both sub-bars. `draggingId` is the
  // tab being dragged; `draggingPane` is the pane it came from
  // (used to suppress drop indicators on the other sub-bar).
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const draggingPane: PaneIdx | null = draggingId
    ? paneByTabId[draggingId] === 1
      ? 1
      : 0
    : null;
  const [dropTarget, setDropTarget] = useState<
    { tabId: string; side: "left" | "right" } | null
  >(null);

  // Context menu — opened by right-click on a tab. Portal-rendered
  // (TabBar has overflow:hidden, would clip a normal absolute menu).
  const [contextMenu, setContextMenu] = useState<{
    tabId: string;
    x: number;
    y: number;
  } | null>(null);


  // Inline rename state. Non-null = the named tab is showing an
  // <input> in place of its label; the user-typed value is `draft`.
  // Entered via context menu "Rename…" OR double-click on the
  // label; committed on Enter/blur; cancelled on Esc.
  const [renaming, setRenaming] = useState<{
    tabId: string;
    draft: string;
  } | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Focus + select-all the FIRST time `renaming` opens — keying off
  // a boolean (not the draft) so per-keystroke draft updates don't
  // re-trigger the effect and re-select the text mid-typing.
  const renamingOpen = renaming !== null;
  useEffect(() => {
    if (renamingOpen) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingOpen]);

  const commitRename = () => {
    if (!renaming) return;
    const next = renaming.draft.trim();
    // Empty string = clear the override; the label falls back to
    // the auto-derived "SYMBOL · TF" from `labelFor`.
    dispatch(renameTab({ id: renaming.tabId, label: next }));
    setRenaming(null);
  };
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!contextMenu) return;
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [contextMenu]);

  const buildMenuItems = (tabId: string): MenuItem[] => {
    const pane: PaneIdx = paneByTabId[tabId] === 1 ? 1 : 0;
    const locked = !!tabs.find((t) => t.id === tabId)?.locked;
    const items: MenuItem[] = [];
    items.push({
      label: "Rename…",
      action: () => {
        const tab = tabs.find((t) => t.id === tabId);
        // Seed with the user-base only — the TF gets re-appended
        // automatically on display.
        setRenaming({
          tabId,
          draft: renameBaseFor(tabId, tab?.label),
        });
      },
    });
    if (pane === 0) {
      if (!splitActive) {
        items.push({
          label: "Open in right pane",
          action: () => dispatch(setSplitTab(tabId)),
          // Can't split with only one tab — the left pane would be
          // empty afterwards. setSplitTab guards this too, but
          // disabling the menu item gives clearer feedback.
          disabled: tabs.length < 2,
        });
      } else {
        items.push({
          label: "Move to right pane",
          action: () =>
            dispatch(moveTabToPane({ tabId, pane: 1 })),
        });
      }
    } else {
      // pane === 1 (right pane)
      items.push({
        label: "Move to left pane",
        action: () => dispatch(moveTabToPane({ tabId, pane: 0 })),
      });
      items.push({
        label: "Close split",
        action: () => dispatch(setSplitTab(null)),
      });
    }
    items.push({
      label: locked ? "Unlock tab" : "Lock tab",
      action: () => dispatch(toggleTabLock(tabId)),
    });
    if (tabs.length > 1) {
      items.push({
        label: "Close tab",
        action: () => dispatch(removeTab(tabId)),
        danger: true,
        // A locked tab is protected — keep the item visible (so the
        // lock is discoverable) but disabled.
        disabled: locked,
      });
    }
    return items;
  };

  /** Display label = user-base + " · " + timeframe. The user-base
   *  is either `tab.label` (an explicit rename) or the symbol; the
   *  timeframe is always appended so the user always sees which TF
   *  a tab is on, even after a custom rename. */
  const labelFor = (id: string, explicit?: string): string => {
    const ui = uiByTab[id];
    if (!ui) return explicit ?? id;
    const base = explicit && explicit.length > 0 ? explicit : ui.symbol;
    return `${base} · ${ui.timeframe}`;
  };
  /** What the rename input is seeded with — just the user-base
   *  half, without the auto-appended timeframe. */
  const renameBaseFor = (id: string, explicit?: string): string => {
    const ui = uiByTab[id];
    if (explicit && explicit.length > 0) return explicit;
    return ui?.symbol ?? id;
  };

  const canClose = tabs.length > 1;

  /** Compute the global `beforeId` for `reorderTabs` given a drop
   *  target (a tab id, plus which half of it the cursor was over).
   *  "side: left" → insert before the hovered tab; "side: right" →
   *  insert before whichever tab follows it in `tabs[]`, or end-of-
   *  list. Returns null if the drop would be a no-op. */
  const computeBeforeId = (
    overTabId: string,
    side: "left" | "right",
  ): string | null | undefined => {
    if (!draggingId) return undefined;
    let beforeId: string | null;
    if (side === "left") {
      beforeId = overTabId;
    } else {
      const idx = tabs.findIndex((t) => t.id === overTabId);
      beforeId =
        idx >= 0 && idx + 1 < tabs.length ? tabs[idx + 1].id : null;
    }
    // Dropping onto the slot the dragging tab already occupies is
    // a no-op.
    if (beforeId === draggingId) return undefined;
    const draggingIdx = tabs.findIndex((t) => t.id === draggingId);
    if (
      beforeId !== null &&
      draggingIdx >= 0 &&
      tabs[draggingIdx + 1]?.id === beforeId
    ) {
      return undefined;
    }
    return beforeId;
  };

  const handleDrop = (overTabId: string, side: "left" | "right") => {
    const beforeId = computeBeforeId(overTabId, side);
    if (beforeId === undefined || !draggingId) return;
    dispatch(reorderTabs({ movingId: draggingId, beforeId }));
  };

  /** Render one sub-bar. Pulled inline to keep state + handlers in
   *  closure; not extracted to its own file because the props would
   *  end up mirroring the entire enclosing state.
   *
   *  The `showWorkspaceSwitcher` flag puts the switcher inside this
   *  bar's right edge — only one bar shows it at a time: the sole
   *  bar in single-pane mode, the right bar in split mode. Keeping
   *  the switcher inside a sub-bar (rather than a sibling of
   *  panesRow) is what makes the tab-bar split visually align with
   *  the chart-pane split: panesRow naturally takes full width, so
   *  the splitRatio-based flex-basis puts the divider at the same
   *  x as the chart pane divider below. */
  const renderSubBar = (
    paneTabs: Tab[],
    pane: PaneIdx,
    activeId: string | null,
    className: string,
    showWorkspaceSwitcher: boolean,
    style?: React.CSSProperties,
  ) => {
    return (
      <div
        className={className}
        role="tablist"
        aria-label={pane === 0 ? "Left pane tabs" : "Right pane tabs"}
        style={style}
        onDragOver={(e) => {
          // Empty area past the last tab in this sub-bar = "drop at
          // end". Only honour drops from the SAME pane (cross-pane
          // drag explicitly disabled — see component-level comment).
          if (!draggingId || draggingPane !== pane) return;
          if ((e.target as HTMLElement).closest(`.${styles.tab}`)) return;
          e.preventDefault();
          const lastTab = paneTabs[paneTabs.length - 1];
          if (lastTab) {
            setDropTarget({ tabId: lastTab.id, side: "right" });
          }
        }}
        onDrop={(e) => {
          if (!draggingId || draggingPane !== pane) return;
          if ((e.target as HTMLElement).closest(`.${styles.tab}`)) return;
          e.preventDefault();
          const lastTab = paneTabs[paneTabs.length - 1];
          if (lastTab) handleDrop(lastTab.id, "right");
          setDraggingId(null);
          setDropTarget(null);
        }}
      >
        {paneTabs.map((tab, idx) => {
          // While the signal view is open it "owns" the active highlight, so no
          // real tab looks selected — clicking one exits signal + selects it.
          const isActive = tab.id === activeId && !signalOpen && !strategiesOpen;
          const isLive = tab.id === liveTabId;
          const isDragging = tab.id === draggingId;
          // Only show drop indicators for same-pane drags.
          const sameOriginPane = draggingPane === pane;
          const showIndicatorLeft =
            sameOriginPane &&
            dropTarget?.tabId === tab.id &&
            dropTarget.side === "left";
          const showIndicatorRight =
            sameOriginPane &&
            dropTarget?.tabId === tab.id &&
            dropTarget.side === "right";
          const isRenaming = renaming?.tabId === tab.id;
          return (
            <Fragment key={tab.id}>
              {idx > 0 ? (
                <span className={styles.tabSep} aria-hidden />
              ) : null}
              <button
              type="button"
              role="tab"
              aria-selected={isActive}
              // Suppress drag while the rename input is mounted —
              // the input needs full mouse control for caret/select.
              draggable={!isRenaming}
              className={[
                styles.tab,
                isActive ? styles.tabActive : "",
                isDragging ? styles.tabDragging : "",
                showIndicatorLeft ? styles.dropLeft : "",
                showIndicatorRight ? styles.dropRight : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                // While renaming, swallow tab-activation clicks so
                // the user can click around inside the input.
                if (isRenaming) return;
                // Selecting a real tab exits the signal / strategies view.
                if (signalOpen) dispatch(setSignalChartOpen(false));
                if (strategiesOpen) dispatch(setStrategiesViewOpen(false));
                dispatch(setActiveTab(tab.id));
              }}
              onDoubleClick={(e) => {
                // Double-click anywhere on the tab (except the ×)
                // opens rename — discoverable shortcut alongside
                // the context-menu entry.
                if (
                  (e.target as HTMLElement).closest(`.${styles.closeBtn}`)
                ) {
                  return;
                }
                e.stopPropagation();
                setRenaming({
                  tabId: tab.id,
                  draft: renameBaseFor(tab.id, tab.label),
                });
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({
                  tabId: tab.id,
                  x: e.clientX,
                  y: e.clientY,
                });
              }}
              title={labelFor(tab.id, tab.label)}
              onDragStart={(e) => {
                if (
                  (e.target as HTMLElement).closest(`.${styles.closeBtn}`)
                ) {
                  e.preventDefault();
                  return;
                }
                setDraggingId(tab.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", tab.id);
              }}
              onDragEnd={() => {
                setDraggingId(null);
                setDropTarget(null);
              }}
              onDragOver={(e) => {
                if (!draggingId || draggingId === tab.id) {
                  e.preventDefault();
                  setDropTarget(null);
                  return;
                }
                // Refuse cross-pane drops: don't preventDefault, so
                // the browser shows the no-drop cursor.
                if (draggingPane !== pane) {
                  setDropTarget(null);
                  return;
                }
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const side =
                  e.clientX < rect.left + rect.width / 2
                    ? "left"
                    : "right";
                if (
                  dropTarget?.tabId !== tab.id ||
                  dropTarget.side !== side
                ) {
                  setDropTarget({ tabId: tab.id, side });
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!draggingId || !dropTarget || draggingPane !== pane) {
                  setDraggingId(null);
                  setDropTarget(null);
                  return;
                }
                handleDrop(dropTarget.tabId, dropTarget.side);
                setDraggingId(null);
                setDropTarget(null);
              }}
            >
              <span className={styles.label}>
                {isLive ? (
                  <span
                    className={styles.liveDot}
                    aria-hidden
                    title="Live"
                  />
                ) : null}
                {tab.locked ? (
                  <span
                    className={styles.lockIcon}
                    aria-hidden
                    title="Locked — protected from closing"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                    >
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  </span>
                ) : null}
                {isRenaming ? (
                  <>
                    <input
                      ref={renameInputRef}
                    type="text"
                    className={styles.renameInput}
                    value={renaming.draft}
                    // Swallow events that would otherwise reach
                    // the surrounding <button> (activation /
                    // drag-start / context-menu).
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setRenaming({
                        tabId: tab.id,
                        draft: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitRename();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        setRenaming(null);
                      }
                      // Stop arrow/space etc. from triggering the
                      // parent button's keyboard activation.
                      e.stopPropagation();
                    }}
                    onBlur={commitRename}
                    // Empty = clear override; show the auto base
                    // (symbol) as a hint so the user knows what
                    // they're resetting to. TF is appended after
                    // the input on display, not inside it.
                    placeholder={renameBaseFor(tab.id)}
                    />
                    <span className={styles.tfSuffix}>
                      {" · "}
                      {uiByTab[tab.id]?.timeframe}
                    </span>
                  </>
                ) : (
                  <>
                    {renameBaseFor(tab.id, tab.label)}
                    {uiByTab[tab.id]?.timeframe ? (
                      <span className={styles.badge}>
                        {uiByTab[tab.id].timeframe}
                      </span>
                    ) : null}
                  </>
                )}
              </span>
              <span
                className={`${styles.closeBtn} ${
                  canClose && !tab.locked ? "" : styles.closeBtnHidden
                }`}
                aria-label="Close tab"
                onClick={(e) => {
                  e.stopPropagation();
                  if (canClose && !tab.locked) dispatch(removeTab(tab.id));
                }}
              >
                ×
              </span>
              </button>
            </Fragment>
          );
        })}
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => dispatch(createTab(undefined, pane))}
          title={
            pane === 0
              ? "New chart tab"
              : "New chart tab in right pane"
          }
          aria-label="New chart tab"
          // renderSubBar runs once PER PANE, so tag only the left one: two
          // elements with the same data-tour makes the anchor ambiguous.
          data-tour={pane === 0 ? "add-tab" : undefined}
        >
          +
        </button>
        {/* Signal chart — a special full-area "tab" for the shared read-only
            bot_all reference. Lives only in the left/pane-0 bar, shown when the
            feature is enabled. Active while the signal view is open; the × (or
            clicking any real tab) closes it. */}
        {pane === 0 && signalEnabled ? (
          <>
            <span className={styles.tabSep} aria-hidden />
            <button
              type="button"
              role="tab"
              aria-selected={signalOpen}
              className={[
                styles.tab,
                styles.tabFeature,
                styles.featSignal,
                signalOpen ? styles.tabActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={signalOnline ? "Signal chart (bot_all)" : "Signal chart — source offline"}
              data-tour="signal-tab"
              onClick={() => {
                if (strategiesOpen) dispatch(setStrategiesViewOpen(false));
                dispatch(setSignalChartOpen(true));
              }}
            >
              <span className={styles.label}>
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    marginRight: 6,
                    background: signalOnline ? "#22c55e" : "#f59e0b",
                    boxShadow: signalOnline ? "0 0 5px #22c55e" : "none",
                  }}
                />
                Signal
              </span>
              {signalOpen ? (
                <span
                  className={styles.closeBtn}
                  aria-label="Close signal chart"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setSignalChartOpen(false));
                  }}
                >
                  ×
                </span>
              ) : null}
            </button>
          </>
        ) : null}
        {/* Strategies — full-area view of the selected strategy bots' orders.
            The dot is green while any strategy has live positions or resting
            orders, amber while the runner is offline. */}
        {pane === 0 && strategiesEnabled ? (
          <>
            <span className={styles.tabSep} aria-hidden />
            <button
              type="button"
              role="tab"
              aria-selected={strategiesOpen}
              className={[
                styles.tab,
                styles.tabFeature,
                styles.featStrategies,
                strategiesOpen ? styles.tabActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={
                strategiesOnline
                  ? `Strategies — ${strategiesActiveCount} active`
                  : "Strategies — runner offline"
              }
              data-tour="strategies-tab"
              onClick={() => {
                if (signalOpen) dispatch(setSignalChartOpen(false));
                dispatch(setStrategiesViewOpen(true));
              }}
            >
              <span className={styles.label}>
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    marginRight: 6,
                    background: !strategiesOnline
                      ? "var(--amber)"
                      : strategiesActiveCount > 0
                        ? "var(--up)"
                        : "var(--faint)",
                    boxShadow:
                      strategiesOnline && strategiesActiveCount > 0
                        ? "0 0 5px var(--up)"
                        : "none",
                  }}
                />
                Strategies
                {strategiesOnline && strategiesActiveCount > 0 ? ` ${strategiesActiveCount}` : ""}
              </span>
              {strategiesOpen ? (
                <span
                  className={styles.closeBtn}
                  aria-label="Close strategies view"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setStrategiesViewOpen(false));
                  }}
                >
                  ×
                </span>
              ) : null}
            </button>
          </>
        ) : null}
        {showWorkspaceSwitcher ? <WorkspaceSwitcher /> : null}
      </div>
    );
  };

  return (
    <div className={`${styles.shell} ${splitActive ? styles.shellSplit : ""}`}>
      <div className={styles.panesRow}>
        {splitActive
          ? renderSubBar(
              leftTabs,
              0,
              activeTabId,
              `${styles.bar} ${styles.barLeft}`,
              // Switcher only on the right sub-bar so the left bar's
              // right edge aligns precisely with the chart-pane
              // divider below.
              false,
              { flexBasis: `${splitRatio * 100}%` },
            )
          : renderSubBar(
              leftTabs,
              0,
              activeTabId,
              `${styles.bar} ${styles.barFull}`,
              // Single-pane mode: switcher lives in this lone bar.
              true,
            )}
        {splitActive
          ? renderSubBar(
              rightTabs,
              1,
              splitTabId,
              `${styles.bar} ${styles.barRight}`,
              // Split mode: switcher sits at the right bar's right
              // end, inside the same bar — the right bar extends
              // all the way to the chart's right edge so the tab-
              // bar split lines up with the chart-pane split.
              true,
              { flexBasis: `${(1 - splitRatio) * 100}%` },
            )
          : null}
      </div>
      {contextMenu
        ? createPortal(
            <div
              ref={menuRef}
              className={styles.contextMenu}
              style={{ top: contextMenu.y, left: contextMenu.x }}
              role="menu"
            >
              {buildMenuItems(contextMenu.tabId).map((item, i) => (
                <button
                  key={i}
                  type="button"
                  role="menuitem"
                  className={`${styles.contextMenuItem} ${
                    item.danger ? styles.contextMenuDanger : ""
                  }`}
                  disabled={item.disabled}
                  onClick={() => {
                    item.action();
                    setContextMenu(null);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
